import React from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { PageHero, SubNav, SectionHeading, NumberedCard, DetailList, Reveal, CTA, Icon } from "../components/Shared";
import { IMAGES, SERVICES, NAV } from "../mock";

const useSubNav = () => {
  const { pathname } = useLocation();
  return NAV[1].children.map((c) => ({ ...c, active: pathname === c.path }));
};

export const WhatWeDoOverview = () => (
  <>
    <PageHero
      eyebrow="What we do"
      title="Clear scope. Reliable delivery. Competitive rates."
      intro="Great North Advantage leverages the expertise and experience of a reliable and agile administrative team to provide shared services, to nonprofit organizations, backed by the purchasing power of over 36 years in business serving over 350 organizations and communities."
      image={IMAGES.accounting2}
    />
    <SubNav items={useSubNav()} />
    <section className="section" data-testid="what-we-do-content">
      <div className="container-x">
        <SectionHeading
          title="A one-stop portal for all of your nonprofit's administrative and management needs."
          intro="We are known for our unsurpassed service, extensive knowledge of the marketplace and a team of tirelessly dedicated administration and management professionals."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <NumberedCard key={s.slug} index={i + 1} title={s.title} text={s.short} to={`/what-we-do/${s.slug}`} icon={s.icon} delay={i * 60} />
          ))}
        </div>
      </div>
    </section>
    <CTA />
  </>
);

export const ServiceDetail = () => {
  const { slug } = useParams();
  const subNav = useSubNav();
  const idx = SERVICES.findIndex((s) => s.slug === slug);
  if (idx === -1) return <Navigate to="/what-we-do" replace />;
  const s = SERVICES[idx];
  const prev = SERVICES[(idx - 1 + SERVICES.length) % SERVICES.length];
  const next = SERVICES[(idx + 1) % SERVICES.length];
  return (
    <>
      <PageHero compact eyebrow="What we do" title={s.title} image={s.image} />
      <SubNav items={subNav} />
      <section className="section" data-testid="service-detail">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal className="sticky top-32">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600 text-white">
                <Icon name={s.icon} size={26} />
              </span>
              <p className="mt-6 font-serif text-2xl leading-snug text-ink-900">{s.lead}</p>
              <Button asChild className="btn-pill mt-8 bg-brand-600 text-white hover:bg-brand-700">
                <Link to="/contact-us">Ask about {s.title.split(" ")[0].toLowerCase()} services <ArrowRight size={15} className="ml-1.5" /></Link>
              </Button>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            {s.body.length > 0 && (
              <Reveal className="prose-gn mb-10">
                {s.body.map((p) => (
                  <p key={p.slice(0, 30)}>{p}</p>
                ))}
              </Reveal>
            )}
            <Reveal>
              <h2 className="mb-6 font-serif text-2xl text-ink-900">{s.listTitle}</h2>
            </Reveal>
            <DetailList items={s.items} />
          </div>
        </div>
        <div className="container-x mt-16 grid gap-4 md:grid-cols-2">
          <Link to={`/what-we-do/${prev.slug}`} className="card-lift group rounded-2xl border border-brand-100 bg-white p-6" data-testid="prev-service">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-ink-500"><ArrowLeft size={13} /> Previous</span>
            <p className="mt-2 font-serif text-xl text-ink-900 group-hover:text-brand-700">{prev.title}</p>
          </Link>
          <Link to={`/what-we-do/${next.slug}`} className="card-lift group rounded-2xl border border-brand-100 bg-white p-6 text-right" data-testid="next-service">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-ink-500">Next <ArrowRight size={13} /></span>
            <p className="mt-2 font-serif text-xl text-ink-900 group-hover:text-brand-700">{next.title}</p>
          </Link>
        </div>
      </section>
      <CTA />
    </>
  );
};
